<template>
  <main v-if="!auth" class="login-screen">
    <q-card class="login-panel" flat>
      <div class="login-header">
        <div class="brand-mark login-mark">
          <q-icon name="auto_awesome" />
        </div>
        <h1>Codex SkyBridge</h1>
        <p>远程 Codex 控制台</p>
      </div>

      <q-form class="login-form" @submit.prevent="login">
        <div class="login-field">
          <span class="field-label">手机号</span>
          <q-input
            v-model="phone"
            outlined
            dense
            autocomplete="tel-national"
            inputmode="numeric"
            maxlength="11"
            placeholder="请输入 11 位大陆手机号"
            @update:model-value="phone = normalizeMainlandPhoneInput($event)"
          >
            <template #prepend>
              <q-icon name="smartphone" />
            </template>
          </q-input>
        </div>

        <div class="login-field">
          <span class="field-label">验证码</span>
          <div class="login-otp-row">
            <q-input v-model="code" outlined dense :placeholder="devCode || '6 位数字'" autocomplete="one-time-code" inputmode="numeric">
              <template #prepend>
                <q-icon name="vpn_key" />
              </template>
            </q-input>
            <q-btn outline color="primary" class="login-otp-btn" label="获取验证码" :loading="busy" @click="requestOtp" />
          </div>
        </div>

        <div class="login-field">
          <span class="field-label">邀请码</span>
          <q-input v-model="inviteCode" outlined dense autocomplete="one-time-code" placeholder="首次登录填写，已授权用户可留空">
            <template #prepend>
              <q-icon name="confirmation_number" />
            </template>
          </q-input>
        </div>

        <q-btn class="login-submit" color="primary" unelevated type="submit" label="登 录" :loading="busy" />

        <q-chip v-if="devCode" class="login-devcode" icon="verified_user">
          开发验证码 {{ devCode }}
        </q-chip>

        <q-banner v-if="error" rounded class="error-banner">
          <template #avatar>
            <q-icon name="error" />
          </template>
          {{ error }}
        </q-banner>
      </q-form>

      <div class="login-foot">
        <q-icon name="lock" />
        <span>端到端加密 · 仅你本人可访问</span>
      </div>
    </q-card>
  </main>

  <q-layout v-else view="lHh Lpr fFf" class="sky-layout">
    <q-drawer
      v-model="navigationDrawer"
      show-if-above
      bordered
      :breakpoint="760"
      :width="drawerWidth"
      class="sky-drawer"
    >
      <div class="drawer-content">
        <div class="sidebar-head">
          <div class="brand-lockup">
            <div class="brand-mark">
              <q-icon name="auto_awesome" />
            </div>
            <h1>Codex SkyBridge</h1>
          </div>
        </div>

        <section class="sidebar-section">
          <div class="section-label">监听端</div>
          <template v-if="loadingWorkspace && hosts.length === 0">
            <q-skeleton height="58px" />
            <q-skeleton height="42px" />
          </template>
          <template v-else-if="hosts.length">
            <button v-for="host in hosts" :key="host.hostId" class="host-badge">
              <q-icon name="desktop_mac" />
              <span>
                <strong>{{ host.deviceName }}</strong>
              </span>
              <i :class="['status-light', { on: host.onlineStatus === 'online' }]" />
            </button>
          </template>
          <div v-else class="empty-line">未连接 Mac 监听端</div>
        </section>

        <div class="sidebar-scroll">
          <section :class="['nav-group', 'chat-group', 'solo-only', { collapsed: !conversationsExpanded }]">
            <div class="section-row">
              <button class="section-toggle" type="button" :aria-expanded="conversationsExpanded" @click="conversationsExpanded = !conversationsExpanded">
                <q-icon :name="conversationsExpanded ? 'expand_more' : 'chevron_right'" />
                <span class="section-label">对话</span>
              </button>
              <q-btn flat round dense icon="add" aria-label="新建对话" @click="startDraftConversation">
                <q-tooltip>新建对话</q-tooltip>
              </q-btn>
            </div>

            <div v-show="conversationsExpanded" class="thread-list">
              <button
                v-for="conversation in projectlessConversations"
                :key="conversation.conversationId"
                :class="['thread-item', { active: conversation.conversationId === selectedConversationId, thinking: conversationIsThinking(conversation) }]"
                @click="selectConversation(conversation)"
              >
                <q-icon name="chat_bubble_outline" />
                <span>
                  <strong>{{ conversationTitle(conversation) }}</strong>
                </span>
              </button>
              <div v-if="!projectlessConversations.length" class="empty-line">暂无对话</div>
            </div>
          </section>
        </div>

        <div class="drawer-footer">
          <div class="user-info">
            <div class="user-avatar">
              <q-icon name="account_circle" />
            </div>
            <div class="user-meta">
              <strong>{{ auth.user?.phoneE164 || "已登录用户" }}</strong>
            </div>
          </div>
          <div class="footer-actions">
            <q-btn
              v-if="isAdminUser"
              flat
              round
              dense
              icon="admin_panel_settings"
              :class="{ active: adminMode }"
              aria-label="管理端"
              @click="openAdminMode"
            >
              <q-tooltip>管理端</q-tooltip>
            </q-btn>
            <q-btn flat round dense icon="settings" aria-label="设置">
              <q-tooltip>设置</q-tooltip>
            </q-btn>
            <q-btn flat round dense icon="logout" aria-label="退出登录" @click="logout">
              <q-tooltip>退出登录</q-tooltip>
            </q-btn>
          </div>
        </div>
      </div>
    </q-drawer>

    <q-header class="chat-topbar">
      <q-toolbar class="chat-toolbar">
        <q-btn flat round dense icon="menu" class="drawer-toggle" aria-label="打开导航" @click="navigationDrawer = true" />
        <div class="chat-title">
          <q-icon :name="adminMode ? 'admin_panel_settings' : 'chat_bubble_outline'" />
          <div>
            <h2>{{ adminMode ? "Admin 管理端" : conversationTitle(selectedConversation) }}</h2>
            <span>{{ adminMode ? "用户、会话与邀请码" : (selectedHost ? `${selectedHost.deviceName} · ${hostStatusLabel(selectedHost.onlineStatus)}` : "等待监听端") }}</span>
          </div>
        </div>
        <q-space />
      </q-toolbar>
    </q-header>

    <q-page-container class="chat-container">
      <q-page class="chat-page">
        <div
          v-if="showConversationLoadingLock"
          class="conversation-loading-lock"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div class="conversation-loading-panel">
            <q-spinner color="primary" size="42px" />
            <strong>载入会话</strong>
            <span>正在同步对话记录</span>
          </div>
        </div>

        <section v-if="adminMode" class="admin-page">
          <div class="admin-hero">
            <div>
              <span class="admin-kicker">Codex SkyBridge</span>
              <h3>Admin 管理端</h3>
              <p>集中查看用户、会话记录和邀请码核销状态。</p>
            </div>
            <q-btn outline color="primary" icon="refresh" label="刷新" :loading="adminLoading" @click="loadAdminDashboard" />
          </div>

          <q-banner v-if="adminError" rounded class="error-banner admin-error">
            <template #avatar>
              <q-icon name="error" />
            </template>
            {{ adminError }}
          </q-banner>

          <div class="admin-stat-grid">
            <article v-for="stat in adminStats" :key="stat.label" class="admin-stat-card">
              <q-icon :name="stat.icon" />
              <span>{{ stat.label }}</span>
              <strong>{{ stat.value }}</strong>
            </article>
          </div>

          <section class="admin-panel invitation-panel">
            <div class="admin-panel-head">
              <div>
                <span class="admin-kicker">访问控制</span>
                <h4>邀请码</h4>
              </div>
              <q-btn color="primary" unelevated icon="add" label="生成邀请码" :loading="adminInviteBusy" @click="createAdminInvitation" />
            </div>
            <div class="admin-invite-form">
              <q-input v-model="adminInviteNote" outlined dense placeholder="备注，例如：给某个用户或设备">
                <template #prepend>
                  <q-icon name="sticky_note_2" />
                </template>
              </q-input>
              <div v-if="generatedInviteCode" class="generated-invite">
                <span>新邀请码</span>
                <code>{{ generatedInviteCode }}</code>
                <q-btn flat round dense :icon="copiedMessageId === 'admin-invite' ? 'done' : 'content_copy'" aria-label="复制邀请码" @click="copyPlainText(generatedInviteCode, 'admin-invite')">
                  <q-tooltip>{{ copiedMessageId === "admin-invite" ? "已复制" : "复制邀请码" }}</q-tooltip>
                </q-btn>
              </div>
            </div>
            <div class="invitation-list">
              <div v-for="invitation in adminInvitations" :key="invitation.invitationId" class="invitation-item">
                <strong>{{ invitation.codePreview }}</strong>
                <span>{{ invitationStatusLabel(invitation.status) }}</span>
                <small>{{ invitation.note || "无备注" }} · {{ formatDate(invitation.createdAt) }}</small>
              </div>
              <div v-if="!adminInvitations.length && !adminLoading" class="empty-line">暂无邀请码</div>
            </div>
          </section>

          <section class="admin-panel">
            <div class="admin-panel-head">
              <div>
                <span class="admin-kicker">用户</span>
                <h4>登录用户</h4>
              </div>
            </div>
            <q-table
              flat
              dense
              class="admin-table admin-users-table"
              row-key="userId"
              :rows="adminUsers"
              :columns="adminUserColumns"
              :loading="adminLoading"
              :grid="adminCardsMode"
              :hide-header="adminCardsMode"
              :hide-bottom="false"
              :pagination="adminCardsMode ? adminMobilePagination : adminTablePagination"
              :rows-per-page-options="adminRowsPerPageOptions"
              rows-per-page-label="每页显示"
              :pagination-label="adminPaginationLabel"
              no-data-label="暂无用户"
            >
              <template #item="props">
                <article class="admin-mobile-row-card">
                  <div class="admin-mobile-row-head">
                    <strong>{{ displayPhone(props.row.phoneE164) }}</strong>
                    <q-chip v-if="props.row.isAdmin" dense square color="cyan-1" text-color="primary">管理员</q-chip>
                  </div>
                  <dl class="admin-mobile-row-meta">
                    <div>
                      <dt>会话</dt>
                      <dd>{{ props.row.conversationCount ?? 0 }}</dd>
                    </div>
                    <div>
                      <dt>运行</dt>
                      <dd>{{ props.row.runCount ?? 0 }}</dd>
                    </div>
                    <div>
                      <dt>最近登录</dt>
                      <dd>{{ formatDate(props.row.lastLoginAt) }}</dd>
                    </div>
                  </dl>
                </article>
              </template>
              <template #body-cell-phoneE164="props">
                <q-td :props="props">
                  <strong>{{ displayPhone(props.row.phoneE164) }}</strong>
                  <q-chip v-if="props.row.isAdmin" dense square color="cyan-1" text-color="primary">管理员</q-chip>
                </q-td>
              </template>
              <template #body-cell-lastLoginAt="props">
                <q-td :props="props">{{ formatDate(props.row.lastLoginAt) }}</q-td>
              </template>
            </q-table>
          </section>

          <section class="admin-panel">
            <div class="admin-panel-head">
              <div>
                <span class="admin-kicker">会话</span>
                <h4>全量会话记录</h4>
              </div>
            </div>
            <q-table
              flat
              dense
              class="admin-table admin-conversations-table"
              row-key="conversationId"
              :rows="adminConversations"
              :columns="adminConversationColumns"
              :loading="adminLoading"
              :grid="adminCardsMode"
              :hide-header="adminCardsMode"
              :hide-bottom="false"
              :pagination="adminCardsMode ? adminMobilePagination : adminTablePagination"
              :rows-per-page-options="adminRowsPerPageOptions"
              rows-per-page-label="每页显示"
              :pagination-label="adminPaginationLabel"
              no-data-label="暂无会话"
            >
              <template #item="props">
                <article class="admin-mobile-row-card conversation-card">
                  <div class="admin-mobile-row-head">
                    <button class="admin-link-button" type="button" @click="openAdminConversation(props.row)">
                      {{ conversationTitle(props.row) }}
                    </button>
                    <q-btn flat round dense icon="visibility" aria-label="查看记录" @click="openAdminConversation(props.row)">
                      <q-tooltip>查看记录</q-tooltip>
                    </q-btn>
                  </div>
                  <dl class="admin-mobile-row-meta">
                    <div>
                      <dt>用户</dt>
                      <dd>{{ displayPhone(props.row.user?.phoneE164) }}</dd>
                    </div>
                    <div>
                      <dt>运行</dt>
                      <dd>{{ props.row.runCount ?? 0 }}</dd>
                    </div>
                    <div>
                      <dt>状态</dt>
                      <dd>{{ statusTitle(props.row.lastRunStatus) }}</dd>
                    </div>
                    <div>
                      <dt>更新时间</dt>
                      <dd>{{ formatDate(props.row.updatedAt) }}</dd>
                    </div>
                  </dl>
                </article>
              </template>
              <template #body-cell-title="props">
                <q-td :props="props">
                  <button class="admin-link-button" type="button" @click="openAdminConversation(props.row)">
                    {{ conversationTitle(props.row) }}
                  </button>
                </q-td>
              </template>
              <template #body-cell-user="props">
                <q-td :props="props">{{ displayPhone(props.row.user?.phoneE164) }}</q-td>
              </template>
              <template #body-cell-updatedAt="props">
                <q-td :props="props">{{ formatDate(props.row.updatedAt) }}</q-td>
              </template>
              <template #body-cell-actions="props">
                <q-td :props="props">
                  <q-btn flat round dense icon="visibility" aria-label="查看记录" @click="openAdminConversation(props.row)">
                    <q-tooltip>查看记录</q-tooltip>
                  </q-btn>
                </q-td>
              </template>
            </q-table>
          </section>

          <q-dialog v-model="adminTranscriptDialog" class="admin-dialog-wrap" :maximized="adminTranscriptMaximized">
            <q-card class="admin-dialog">
              <div class="admin-dialog-head">
                <div>
                  <span class="admin-kicker">会话记录</span>
                  <h4>{{ conversationTitle(adminTranscriptConversation) }}</h4>
                  <p>{{ displayPhone(adminTranscriptConversation?.user?.phoneE164) }} · {{ formatDate(adminTranscriptConversation?.updatedAt) }}</p>
                </div>
                <q-btn flat round dense icon="close" aria-label="关闭" v-close-popup />
              </div>
              <q-separator />
              <div
                ref="adminTranscriptScroll"
                class="admin-transcript"
                @pointerdown="beginAdminTranscriptDrag"
                @pointermove="moveAdminTranscriptDrag"
                @pointerup="endAdminTranscriptDrag"
                @pointercancel="endAdminTranscriptDrag"
                @lostpointercapture="endAdminTranscriptDrag"
                @wheel.stop
                role="region"
                aria-label="会话记录内容"
                tabindex="0"
              >
                <q-skeleton v-if="adminTranscriptLoading" height="72px" />
                <div v-else-if="adminTranscriptError" class="admin-transcript-error">
                  <q-icon name="error_outline" />
                  <span>{{ adminTranscriptError }}</span>
                  <q-btn flat dense color="primary" icon="refresh" label="重试" @click="retryAdminTranscript" />
                </div>
                <div v-if="!adminTranscriptLoading && !adminTranscriptError && (adminTranscriptHasMore || adminTranscriptLoadingMore)" class="admin-transcript-page-controls">
                  <q-btn
                    flat
                    dense
                    color="primary"
                    icon="history"
                    label="加载更早记录"
                    :loading="adminTranscriptLoadingMore"
                    :disable="adminTranscriptLoadingMore"
                    @click="loadEarlierAdminTranscript"
                  />
                </div>
                <article v-for="item in adminTranscriptItems" :key="item.id" :class="['admin-transcript-item', item.kind, item.role]">
                  <strong>{{ adminTranscriptTitle(item) }}</strong>
                  <div v-if="item.kind === 'message'" class="markdown-body" v-html="renderMarkdown(item.text)" />
                  <span v-else>{{ adminActivityLabel(item) }}</span>
                </article>
                <div v-if="!adminTranscriptLoading && !adminTranscriptError && adminTranscriptItems.length === 0" class="empty-line">暂无记录</div>
              </div>
            </q-card>
          </q-dialog>
        </section>

        <template v-else>
        <section ref="conversationScroll" class="conversation-canvas" @scroll.passive="handleConversationScroll">
          <div class="conversation-inner">
            <div v-if="!loadingConversation && conversationItems.length === 0" class="empty-state">
              <div class="empty-mark">
                <q-icon name="auto_awesome" />
              </div>
              <strong>Codex 已就绪</strong>
              <span>发送消息后，回复会保留在当前单独会话中。</span>
            </div>

            <template v-if="loadingConversation && conversationItems.length === 0">
              <q-skeleton v-for="item in 5" :key="item" height="64px" class="message-skeleton" />
            </template>

            <article v-for="item in conversationItems" :key="item.id" :class="['transcript-item', item.kind, item.role, { queued: item.queued }]">
              <template v-if="item.kind === 'message'">
                <div :class="['message-body', { streaming: item.streaming }]">
                  <div v-if="item.queued || item.ephemeral" class="message-author">
                    <q-chip v-if="item.queued" dense square color="cyan-1" text-color="primary">已排队</q-chip>
                    <q-chip v-if="item.ephemeral" dense square class="ephemeral-chip">仅本页</q-chip>
                    <q-btn
                      v-if="item.queued"
                      flat
                      round
                      dense
                      icon="edit"
                      aria-label="编辑排队消息"
                      @click="editQueuedTurn(item.queueId)"
                    >
                      <q-tooltip>编辑排队消息</q-tooltip>
                    </q-btn>
                    <q-btn
                      v-if="item.queued"
                      flat
                      round
                      dense
                      icon="close"
                      aria-label="移除排队消息"
                      @click="removeQueuedTurn(item.queueId)"
                    >
                      <q-tooltip>移除排队消息</q-tooltip>
                    </q-btn>
                  </div>
                  <div class="markdown-body" v-html="renderMarkdown(item.text)" />
                  <div v-if="item.attachments?.length" class="message-attachments">
                    <span v-for="attachment in item.attachments" :key="attachment.fileId ?? attachment.name">
                      <q-icon name="attach_file" />
                      {{ attachment.name }}
                    </span>
                  </div>
                </div>
                <div v-if="messageCopyText(item)" class="message-copy-row">
                  <q-btn
                    flat
                    round
                    dense
                    class="message-copy-button"
                    :icon="copiedMessageId === item.id ? 'done' : 'content_copy'"
                    :aria-label="copiedMessageId === item.id ? '已复制消息文本' : '复制消息文本'"
                    @click="copyMessageText(item)"
                  >
                    <q-tooltip>{{ copiedMessageId === item.id ? "已复制" : "复制文本" }}</q-tooltip>
                  </q-btn>
                </div>
              </template>

              <div v-else-if="item.kind === 'reasoning'" class="activity-line thinking-line">
                <div class="thinking-pill">
                  <span>Codex 正在思考</span>
                </div>
              </div>

              <q-expansion-item
                v-else-if="item.kind === 'command'"
                dense
                class="activity-card command-card"
                expand-icon="keyboard_arrow_down"
                :model-value="activityExpanded(item)"
                @update:model-value="setActivityExpanded(item, $event)"
              >
                <template #header>
                  <q-item-section avatar>
                    <q-icon name="terminal" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ commandTitle(item) }}</q-item-label>
                    <q-item-label v-if="compactPath(item.cwd)" caption>{{ compactPath(item.cwd) }}</q-item-label>
                  </q-item-section>
                </template>
                <pre class="activity-output">{{ item.output || "暂无输出" }}</pre>
              </q-expansion-item>

              <div v-else-if="item.kind === 'fileChange'" class="activity-card file-card">
                <q-icon name="difference" />
                <div class="activity-main">
                  <strong>文件变更</strong>
                  <span>{{ fileChangeSummary(item) }}</span>
                  <div class="file-change-list">
                    <span v-for="change in item.changes" :key="change.id">
                      <q-icon :name="fileChangeIcon(change.kind)" />
                      {{ fileChangeKindLabel(change.kind) }} {{ compactPath(change.path) }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-else-if="item.kind === 'hook'" class="activity-card hook-card">
                <q-icon name="webhook" />
                <div class="activity-main">
                  <div class="activity-heading">
                    <strong>{{ item.title || "Hook 提示" }}</strong>
                    <q-chip v-if="item.status" dense :color="hookStatusTone(item.status)" text-color="white">
                      {{ hookStatusLabel(item.status) }}
                    </q-chip>
                  </div>
                  <span v-if="item.text">{{ item.text }}</span>
                  <small v-if="hookSubtitle(item)">{{ hookSubtitle(item) }}</small>
                </div>
              </div>

              <q-expansion-item
                v-else-if="item.kind === 'diff'"
                dense
                class="activity-card diff-card"
                expand-icon="keyboard_arrow_down"
                :model-value="activityExpanded(item)"
                @update:model-value="setActivityExpanded(item, $event)"
              >
                <template #header>
                  <q-item-section avatar>
                    <q-icon name="difference" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ diffSummary(item) }}</q-item-label>
                    <q-item-label caption>{{ diffSubtitle(item) }}</q-item-label>
                  </q-item-section>
                </template>
                <div class="diff-body">
                  <div class="diff-file-list">
                    <span v-for="file in item.files" :key="file.id">
                      <q-icon name="description" />
                      {{ compactPath(file.path) }}
                      <small>+{{ file.additions }} -{{ file.deletions }}</small>
                    </span>
                  </div>
                  <pre class="diff-preview"><span
                    v-for="(line, index) in diffPreviewLines(item)"
                    :key="`${item.id}:${index}`"
                    :class="diffLineClass(line)"
                  >{{ line || " " }}</span></pre>
                </div>
              </q-expansion-item>

              <q-expansion-item
                v-else-if="item.kind === 'tool'"
                dense
                class="activity-card tool-card"
                expand-icon="keyboard_arrow_down"
                :model-value="activityExpanded(item)"
                @update:model-value="setActivityExpanded(item, $event)"
              >
                <template #header>
                  <q-item-section avatar>
                    <q-icon :name="toolIcon(item)" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ toolTitle(item) }}</q-item-label>
                    <q-item-label v-if="toolSubtitle(item)" caption>{{ toolSubtitle(item) }}</q-item-label>
                  </q-item-section>
                </template>
                <div class="tool-detail">
                  <div v-if="item.input" class="tool-detail-block">
                    <span>输入</span>
                    <pre>{{ formatToolPayload(item.input) }}</pre>
                  </div>
                  <div v-if="item.output" class="tool-detail-block">
                    <span>输出</span>
                    <pre>{{ formatToolPayload(item.output) }}</pre>
                  </div>
                  <div v-if="item.error" class="tool-detail-block error">
                    <span>错误</span>
                    <pre>{{ item.error }}</pre>
                  </div>
                  <span v-if="!item.input && !item.output && !item.error" class="tool-empty">暂无详细信息</span>
                </div>
              </q-expansion-item>

              <div v-else-if="item.kind === 'asset'" class="activity-card asset-card">
                <div class="asset-icon-shell">
                  <q-icon :name="assetIcon(item)" />
                </div>
                <div class="asset-content">
                  <div class="asset-title-row">
                    <strong>{{ assetTitle(item) }}</strong>
                    <span v-if="assetBadge(item)" class="asset-badge">{{ assetBadge(item) }}</span>
                  </div>
                  <span class="asset-meta">{{ assetMeta(item) }}</span>
                  <a v-if="item.url" class="asset-link" :href="item.url" target="_blank" rel="noreferrer">{{ assetContext(item) }}</a>
                  <code v-else-if="assetContext(item)" class="asset-path">{{ assetContext(item) }}</code>
                </div>
                <div v-if="item.objectKey" class="asset-actions">
                  <q-btn
                    flat
                    round
                    dense
                    icon="download"
                    :loading="downloadingArtifactId === item.id"
                    :disable="downloadingArtifactId === item.id"
                    :aria-label="`下载 ${assetTitle(item)}`"
                    @click="downloadArtifact(item)"
                  >
                    <q-tooltip>下载文件</q-tooltip>
                  </q-btn>
                </div>
              </div>

              <div v-else-if="item.kind === 'subAgent'" class="activity-card sub-agent-card">
                <q-icon :name="subAgentIcon(item.status)" />
                <div class="activity-main">
                  <strong>{{ subAgentTitle(item.status) }}</strong>
                  <span>{{ subAgentSubtitle(item) }}</span>
                </div>
              </div>

              <div v-else-if="item.kind === 'autoReview'" class="activity-card auto-review-card">
                <q-icon :name="autoReviewIcon(item.status)" />
                <div class="activity-main">
                  <strong>{{ autoReviewTitle(item.status) }}</strong>
                  <span>{{ autoReviewSubtitle(item) }}</span>
                  <div class="auto-review-meta">
                    <q-chip v-if="item.riskLevel" dense square>{{ autoReviewRiskLabel(item.riskLevel) }}</q-chip>
                    <q-chip v-if="item.decisionSource" dense square>来源：{{ item.decisionSource }}</q-chip>
                  </div>
                </div>
              </div>

              <div v-else-if="item.kind === 'webSearch'" class="activity-card search-card">
                <q-icon name="travel_explore" />
                <div class="activity-main">
                  <strong>网页搜索</strong>
                  <span>{{ item.query }}</span>
                </div>
              </div>

              <div v-else-if="item.kind === 'plan'" class="activity-card plan-card">
                <q-icon name="checklist" />
                <div class="activity-main">
                  <strong>任务计划</strong>
                  <span v-if="item.explanation">{{ item.explanation }}</span>
                  <p v-if="item.text" class="plan-text">{{ item.text }}</p>
                  <ol v-if="item.steps?.length" class="plan-list">
                    <li v-for="step in item.steps" :key="step.id" :class="step.status">
                      <q-icon :name="planStepIcon(step.status)" />
                      {{ step.text }}
                    </li>
                  </ol>
                </div>
              </div>

              <div v-else-if="item.kind === 'approval'" class="activity-card approval-card">
                <q-icon name="verified_user" />
                <div class="activity-main">
                  <strong>需要你确认</strong>
                  <span>{{ approvalSummary(item.approval) }}</span>
                  <code v-if="item.approval.command">{{ item.approval.command }}</code>
                  <div class="approval-actions" v-if="!item.approval.decision">
                    <q-btn dense unelevated color="primary" label="批准" @click="decideApproval(item, 'approved')" />
                    <q-btn dense flat color="negative" label="拒绝" @click="decideApproval(item, 'denied')" />
                  </div>
                  <q-chip v-else dense color="teal-1" text-color="primary">{{ approvalStatusLabel(item.approval) }}</q-chip>
                </div>
              </div>

              <div v-else-if="item.kind === 'error'" class="activity-card error-card">
                <q-icon name="error" />
                <div class="activity-main">
                  <strong>运行失败</strong>
                  <span>{{ item.text }}</span>
                </div>
              </div>

              <div v-else-if="item.kind === 'notice'" class="activity-line">
                <q-icon :name="item.icon || 'info'" />
                <span>{{ item.text }}</span>
              </div>
            </article>

            <div v-if="showThinkingPanel" class="thinking-panel">
              <div class="thinking-pill">
                <span>Codex 正在思考</span>
              </div>
            </div>
          </div>
        </section>

        <form class="composer" @submit.prevent="submitTurn">
          <div v-if="activePendingApproval" class="approval-blocker">
            <div class="approval-blocker-copy">
              <q-icon name="verified_user" />
              <div>
                <strong>Codex 等待你确认</strong>
                <span>{{ approvalSummary(activePendingApproval.approval) }}</span>
              </div>
            </div>
            <code v-if="activePendingApproval.approval.command">{{ activePendingApproval.approval.command }}</code>
            <div class="approval-blocker-actions">
              <q-btn dense unelevated color="primary" label="批准" :loading="runControlBusy" :disable="runControlBusy" @click="decideApproval(activePendingApproval, 'approved')" />
              <q-btn dense flat color="negative" label="拒绝" :disable="runControlBusy" @click="decideApproval(activePendingApproval, 'denied')" />
            </div>
          </div>
          <div v-if="runIsActive || canResumeRun" class="run-action-strip">
            <div>
              <q-icon :name="runActionIcon" />
              <span>{{ runActionText }}</span>
            </div>
            <q-btn
              v-if="runCanInterrupt"
              flat
              round
              dense
              icon="stop_circle"
              :loading="runControlBusy"
              :disable="runControlBusy"
              aria-label="中断运行"
              @click="interruptActiveRun"
            >
              <q-tooltip>中断当前运行</q-tooltip>
            </q-btn>
            <q-btn
              v-else-if="canResumeRun"
              flat
              round
              dense
              icon="replay"
              :loading="runControlBusy"
              :disable="runControlBusy"
              aria-label="继续运行"
              @click="resumeActiveRun"
            >
              <q-tooltip>继续上一次运行</q-tooltip>
            </q-btn>
          </div>
          <div v-if="pendingAttachments.length" class="attachment-tray">
            <q-chip
              v-for="attachment in pendingAttachments"
              :key="attachment.id"
              dense
              removable
              color="teal-1"
              text-color="primary"
              icon="attach_file"
              @remove="removeAttachment(attachment.id)"
            >
              {{ attachment.name }} · {{ formatBytes(attachment.size) }}
            </q-chip>
          </div>
          <div v-if="fileMentions.length" class="reference-tray">
            <q-chip
              v-for="mention in fileMentions"
              :key="mention.token"
              dense
              removable
              color="cyan-1"
              text-color="primary"
              icon="alternate_email"
              @remove="removeFileMention(mention.token)"
            >
              {{ mention.label }}
            </q-chip>
          </div>
          <div v-if="slashCommandsOpen" class="slash-command-menu">
            <button
              v-for="(command, index) in filteredSlashCommands"
              :key="command.name"
              type="button"
              class="slash-command-item"
              :class="{ selected: index === slashCommandIndex }"
              @mousedown.prevent="applySlashCommand(command)"
            >
              <q-icon :name="command.icon" />
              <span>
                <strong>/{{ command.name }}</strong>
                <small>{{ command.description }}</small>
              </span>
            </button>
          </div>
          <q-input
            v-model="message"
            type="textarea"
            autogrow
            borderless
            class="composer-input"
            :placeholder="runIsActive ? '当前运行中，发送后将加入队列' : '向 Codex 发送消息'"
            :disable="composerLocked"
            @keydown="handleComposerKeydown"
          />
          <q-btn class="attach-button" flat round icon="add" :disable="composerLocked" aria-label="添加附件" @click="attachmentInput?.click()">
            <q-tooltip>添加附件</q-tooltip>
          </q-btn>
          <input ref="attachmentInput" class="hidden-file-input" type="file" multiple @change="handleAttachmentInput" />
          <q-btn
            class="send-button"
            color="primary"
            round
            unelevated
            :icon="runIsActive ? 'playlist_add' : 'arrow_upward'"
            :disable="composerLocked || (!message.trim() && pendingAttachments.length === 0)"
            :loading="busy"
            type="submit"
            :aria-label="runIsActive ? '加入队列' : '发送'"
          />
        </form>
        </template>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useQuasar } from "quasar";
import { RunStatus } from "@skybridge/shared";
import { buildCodexTranscript } from "./codex-transcript.js";
import { renderMarkdown } from "./markdown.js";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";
const TERMINAL_STATUSES = new Set([
  RunStatus.SUCCEEDED,
  RunStatus.FAILED,
  RunStatus.CANCELLED,
  RunStatus.INTERRUPTED,
  RunStatus.EXPIRED
]);

const STATUS_LABELS = {
  ready: "就绪",
  active: "活跃",
  queued: "排队中",
  claimed: "已接收",
  downloading_input: "准备输入",
  starting_codex: "启动 Codex",
  running: "运行中",
  waiting_approval: "等待审批",
  uploading_final: "上传结果",
  reconciling: "同步中",
  succeeded: "已完成",
  failed: "失败",
  cancelled: "已取消",
  interrupted: "已中断",
  host_unavailable: "监听端不可用",
  needs_resume: "等待继续",
  expired: "已过期"
};

const HOST_STATUS_LABELS = {
  online: "在线",
  offline: "离线",
  reconciling: "同步中"
};
const RESUMABLE_STATUSES = new Set([
  RunStatus.FAILED,
  RunStatus.INTERRUPTED,
  RunStatus.HOST_UNAVAILABLE,
  RunStatus.NEEDS_RESUME,
  RunStatus.EXPIRED,
  RunStatus.CANCELLED
]);
const THINKING_RUN_STATUSES = new Set([
  RunStatus.QUEUED,
  RunStatus.CLAIMED,
  RunStatus.DOWNLOADING_INPUT,
  RunStatus.STARTING_CODEX,
  RunStatus.RUNNING,
  RunStatus.UPLOADING_FINAL
]);
const QUEUED_TURNS_STORAGE_KEY = "skybridge.queuedTurns";
const ACTIVITY_EXPANSION_STORAGE_KEY = "skybridge.activityExpansion";
const DRAFT_CONVERSATION_ID = "draft:new-conversation";
const MAX_PERSISTED_QUEUED_TURNS = 25;
const MAX_ACTIVITY_EXPANSION_ENTRIES = 250;
const SLASH_COMMANDS = [
  {
    name: "plan",
    icon: "schema",
    description: "先给出实施计划",
    instruction: "请先给出简洁、可执行的实施计划，等待用户确认后再进行具体修改。"
  },
  {
    name: "review",
    icon: "rate_review",
    description: "按代码审查模式检查",
    mode: "review"
  },
  {
    name: "clear",
    icon: "backspace",
    description: "清空当前输入",
    local: true
  }
];

const $q = useQuasar();
const auth = useLocalState("skybridge.auth", null);
const composerHistory = useLocalState("skybridge.composerHistory", []);
const activityExpansion = useLocalState(ACTIVITY_EXPANSION_STORAGE_KEY, {});
const phone = ref("");
const code = ref("");
const inviteCode = ref("");
const devCode = ref("");
const hosts = ref([]);
const conversations = ref([]);
const draftConversation = ref(null);
const selectedConversationId = ref("");
const loadedConversationId = ref("");
const runDetails = ref([]);
const message = ref("");
const busy = ref(false);
const runControlBusy = ref(false);
const loadingWorkspace = ref(Boolean(auth.value));
const loadingConversation = ref(false);
const lockingConversationLoad = ref(false);
const error = ref("");
const conversationScroll = ref(null);
const attachmentInput = ref(null);
const pendingAttachments = ref([]);
const downloadingArtifactId = ref("");
const copiedMessageId = ref("");
const queuedTurns = ref(readQueuedTurns());
const conversationsExpanded = ref(true);
const navigationDrawer = ref(false);
const adminMode = ref(false);
const adminLoading = ref(false);
const adminInviteBusy = ref(false);
const adminError = ref("");
const adminOverview = ref(null);
const adminUsers = ref([]);
const adminConversations = ref([]);
const adminInvitations = ref([]);
const adminInviteNote = ref("");
const generatedInviteCode = ref("");
const adminTranscriptDialog = ref(false);
const adminTranscriptLoading = ref(false);
const adminTranscriptLoadingMore = ref(false);
const adminTranscriptError = ref("");
const adminTranscriptHasMore = ref(false);
const adminTranscriptCursor = ref(null);
const adminTranscriptConversation = ref(null);
const adminTranscriptRuns = ref([]);
const adminTranscriptScroll = ref(null);
const adminTranscriptDrag = {
  active: false,
  moved: false,
  pointerId: null,
  scroller: null,
  startX: 0,
  startY: 0,
  scrollTop: 0
};

let pollTimer = null;
let streamAbortController = null;
let copyResetTimer = null;
let queueDrainBusy = false;
let terminalReloadedRunIds = new Set();
const historyCursor = ref(-1);
const historyDraft = ref("");
const slashCommandIndex = ref(0);
const slashMenuDismissed = ref(false);
const shouldAutoScroll = ref(true);

const api = makeApi(auth);
const drawerWidth = computed(() => ($q.screen.lt.sm ? Math.min(280, Math.max(248, Math.round($q.screen.width * 0.76))) : 280));
const isAdminUser = computed(() => Boolean(auth.value?.user?.isAdmin || auth.value?.user?.phoneE164 === "+8615210157521"));
const selectedConversation = computed(() => {
  if (isDraftConversationId(selectedConversationId.value)) {
    return draftConversation.value;
  }
  return conversations.value.find((conversation) => conversation.conversationId === selectedConversationId.value) ?? null;
});
const projectlessConversations = computed(() => {
  const realConversations = conversations.value.filter((conversation) => !conversation.projectId);
  return draftConversation.value ? [draftConversation.value, ...realConversations] : realConversations;
});
const activeDetail = computed(() => runDetails.value.at(-1) ?? null);
const activeRun = computed(() => activeDetail.value?.run ?? null);
const selectedHost = computed(() => {
  const hostId = selectedConversation.value?.hostId;
  return hosts.value.find((host) => host.hostId === hostId) ?? hosts.value[0] ?? null;
});
const runIsActive = computed(() => Boolean(activeRun.value && !TERMINAL_STATUSES.has(activeRun.value.status)));
const canResumeRun = computed(() => Boolean(activeRun.value && RESUMABLE_STATUSES.has(activeRun.value.status)));
const runCanInterrupt = computed(() => Boolean(runIsActive.value && !canResumeRun.value && activeRun.value?.status !== RunStatus.RECONCILING));
const runActionText = computed(() => {
  if (activePendingApproval.value) return "等待确认 · Codex 暂停在审批点";
  if (canResumeRun.value) return recoveryActionText(activeRun.value?.status);
  if (activeRun.value?.status === RunStatus.RECONCILING) return "同步中 · 正在确认 Mac 端状态";
  if (runIsActive.value) return `${statusTitle(activeRun.value?.status)} · Codex 正在处理`;
  return "";
});
const runActionIcon = computed(() => {
  if (activePendingApproval.value) return "verified_user";
  if (activeRun.value?.status === RunStatus.HOST_UNAVAILABLE) return "cloud_off";
  if (activeRun.value?.status === RunStatus.RECONCILING) return "sync_problem";
  if (canResumeRun.value) return "replay";
  return "sync";
});
const activeQueuedTurns = computed(() => queuedTurns.value.filter((turn) => turn.conversationId === selectedConversationId.value));
const conversationItems = computed(() => [
  ...buildCodexTranscript(runDetails.value, { terminalStatuses: TERMINAL_STATUSES }),
  ...activeQueuedTurns.value.map(queuedTurnToTranscriptItem)
]);
const activePendingApproval = computed(() => {
  const runId = activeRun.value?.runId;
  if (!runId) return null;
  return conversationItems.value.find((item) => (
    item.kind === "approval" &&
    item.runId === runId &&
    !item.approval?.decision
  )) ?? null;
});
const composerLocked = computed(() => Boolean(busy.value));
const slashCommandQuery = computed(() => {
  const value = message.value.trimStart();
  const match = value.match(/^\/([A-Za-z]*)$/);
  return match ? match[1].toLowerCase() : null;
});
const filteredSlashCommands = computed(() => {
  if (slashCommandQuery.value === null) return [];
  return SLASH_COMMANDS.filter((command) => command.name.startsWith(slashCommandQuery.value));
});
const slashCommandsOpen = computed(() => Boolean(
  !composerLocked.value &&
  !slashMenuDismissed.value &&
  slashCommandQuery.value !== null &&
  filteredSlashCommands.value.length
));
const hasLiveActivity = computed(() => conversationItems.value.some((item) => {
  if (item.kind === "reasoning") return item.status === "in_progress";
  if (item.kind === "command" || item.kind === "tool") return item.status === "in_progress";
  return false;
}));
const showThinkingPanel = computed(() => Boolean(
  runIsActive.value &&
  !hasLiveActivity.value &&
  !activePendingApproval.value &&
  !canResumeRun.value &&
  activeRun.value?.status !== RunStatus.RECONCILING
));
const showConversationLoadingLock = computed(() => Boolean(
  !adminMode.value &&
  lockingConversationLoad.value
));
const fileMentions = computed(() => extractFileMentions(message.value));
const adminStats = computed(() => {
  const overview = adminOverview.value ?? {};
  return [
    { label: "用户", value: overview.users ?? 0, icon: "group" },
    { label: "会话", value: overview.conversations ?? 0, icon: "forum" },
    { label: "运行", value: overview.runs ?? 0, icon: "bolt" },
    { label: "待用邀请码", value: overview.pendingInvitations ?? 0, icon: "confirmation_number" }
  ];
});
const adminTranscriptItems = computed(() => buildCodexTranscript(adminTranscriptRuns.value, { terminalStatuses: TERMINAL_STATUSES }));
const adminCardsMode = computed(() => $q.screen.width < 760);
const adminTranscriptMaximized = computed(() => $q.screen.width <= 760);
const adminTablePagination = { rowsPerPage: 8 };
const adminMobilePagination = { rowsPerPage: 6 };
const adminRowsPerPageOptions = [6, 8, 12, 24];

const adminUserColumns = [
  { name: "phoneE164", label: "手机号", field: "phoneE164", align: "left", sortable: true },
  { name: "conversationCount", label: "会话", field: "conversationCount", align: "right", sortable: true },
  { name: "runCount", label: "运行", field: "runCount", align: "right", sortable: true },
  { name: "lastLoginAt", label: "最近登录", field: "lastLoginAt", align: "left", sortable: true }
];

const adminConversationColumns = [
  { name: "title", label: "标题", field: "title", align: "left", sortable: true },
  { name: "user", label: "用户", field: (row) => row.user?.phoneE164 ?? "", align: "left", sortable: true },
  { name: "runCount", label: "运行", field: "runCount", align: "right", sortable: true },
  { name: "lastRunStatus", label: "状态", field: "lastRunStatus", align: "left", sortable: true, format: (value) => statusTitle(value) },
  { name: "updatedAt", label: "更新时间", field: "updatedAt", align: "left", sortable: true },
  { name: "actions", label: "", field: "conversationId", align: "right" }
];

function adminPaginationLabel(firstRowIndex, endRowIndex, totalRowsNumber) {
  return `${firstRowIndex}-${endRowIndex} / ${totalRowsNumber}`;
}

onMounted(() => {
  updateResponsiveDrawers();
  window.addEventListener("resize", updateResponsiveDrawers);
  if (auth.value) {
    latestAuth = auth.value;
    syncAuthenticatedUser().finally(() => {
      if (auth.value) {
        refreshWorkspace();
      } else {
        loadingWorkspace.value = false;
      }
    });
  }
});

onBeforeUnmount(() => {
  stopRunWatch();
  removeAdminTranscriptDragListeners();
  if (copyResetTimer) {
    clearTimeout(copyResetTimer);
  }
  window.removeEventListener("resize", updateResponsiveDrawers);
});

watch(
  () => runIsActive.value ? activeRun.value?.runId : "",
  (runId) => {
    stopRunWatch();
    if (!runId) return;
    startRunStream(runId);
  },
  { immediate: true }
);

watch(conversationItems, () => {
  scrollToBottom({ force: false });
}, { deep: true });

watch(
  () => [runIsActive.value, selectedConversationId.value, loadedConversationId.value, activeRun.value?.status ?? "", queuedTurns.value.length],
  () => {
    drainQueuedTurns().catch((err) => {
      error.value = err.message;
    });
  }
);

watch(queuedTurns, persistQueuedTurns, { deep: true });

watch(message, () => {
  historyCursor.value = -1;
  historyDraft.value = "";
  slashCommandIndex.value = 0;
  slashMenuDismissed.value = false;
});

watch(isAdminUser, (allowed) => {
  if (!allowed) {
    resetAdminState();
  }
});

async function requestOtp() {
  try {
    busy.value = true;
    error.value = "";
    const result = await makeApi().post("/api/auth/sms/send", { phone: phoneForAuth() });
    devCode.value = result.devCode ?? "";
  } catch (err) {
    error.value = err.message;
  } finally {
    busy.value = false;
  }
}

async function login() {
  try {
    busy.value = true;
    error.value = "";
    const cleanInviteCode = String(inviteCode.value ?? "").trim();
    const result = await makeApi().post("/api/auth/sms/login", {
      phone: phoneForAuth(),
      code: String(code.value ?? "").trim(),
      deviceLabel: "web-quasar",
      ...(cleanInviteCode ? { inviteCode: cleanInviteCode } : {})
    });
    setAuth(result);
    inviteCode.value = "";
    await refreshWorkspace();
  } catch (err) {
    error.value = err.message;
  } finally {
    busy.value = false;
  }
}

function normalizeMainlandPhoneInput(value) {
  return String(value ?? "").replace(/\D/g, "").slice(0, 11);
}

function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function phoneForAuth() {
  const digits = normalizeMainlandPhoneInput(phone.value);
  phone.value = digits;
  if (!/^1[3-9]\d{9}$/.test(digits)) {
    throw new Error("请输入 11 位中国大陆手机号。");
  }
  return `+86${digits}`;
}

async function syncAuthenticatedUser() {
  if (!auth.value) return;
  try {
    const result = await api.get("/api/auth/me");
    if (result.user && auth.value) {
      setAuth({ ...auth.value, user: result.user, role: result.role ?? auth.value.role });
    }
  } catch (err) {
    error.value = err.message;
  }
}

async function openAdminMode() {
  if (!isAdminUser.value) {
    error.value = "当前账号没有管理端权限。";
    return;
  }
  adminMode.value = true;
  closeNavigationForMobile();
  await loadAdminDashboard();
}

async function loadAdminDashboard() {
  if (!auth.value || !isAdminUser.value) return;
  adminLoading.value = true;
  adminError.value = "";
  try {
    const result = await api.get("/api/admin");
    adminOverview.value = result.overview ?? null;
    adminUsers.value = result.users ?? [];
    adminConversations.value = result.conversations ?? [];
    adminInvitations.value = result.invitations ?? [];
  } catch (err) {
    adminError.value = err.message;
  } finally {
    adminLoading.value = false;
  }
}

async function createAdminInvitation() {
  if (adminInviteBusy.value) return;
  adminInviteBusy.value = true;
  adminError.value = "";
  try {
    const result = await api.post("/api/admin/invitations", { note: adminInviteNote.value.trim() });
    generatedInviteCode.value = result.code;
    adminInviteNote.value = "";
    await loadAdminDashboard();
  } catch (err) {
    adminError.value = err.message;
  } finally {
    adminInviteBusy.value = false;
  }
}

async function openAdminConversation(conversation) {
  adminTranscriptDialog.value = true;
  adminTranscriptConversation.value = conversation;
  adminTranscriptRuns.value = [];
  adminTranscriptError.value = "";
  adminTranscriptHasMore.value = false;
  adminTranscriptCursor.value = null;
  await loadAdminTranscriptPage(conversation, { replace: true });
}

async function loadAdminTranscriptPage(conversation, { replace = false } = {}) {
  if (!conversation || adminTranscriptLoading.value || adminTranscriptLoadingMore.value) return;
  const scroller = adminTranscriptScroll.value;
  const previousHeight = !replace ? scroller?.scrollHeight ?? 0 : 0;
  const previousTop = !replace ? scroller?.scrollTop ?? 0 : 0;
  if (replace) adminTranscriptLoading.value = true;
  else adminTranscriptLoadingMore.value = true;
  adminTranscriptError.value = "";
  try {
    const query = new URLSearchParams({ limit: "2" });
    if (!replace && adminTranscriptCursor.value) query.set("before", adminTranscriptCursor.value);
    const result = await api.get(`/api/admin/conversations/${conversation.conversationId}/transcript?${query}`);
    adminTranscriptConversation.value = result.conversation;
    const incoming = result.runs ?? [];
    if (replace) {
      adminTranscriptRuns.value = incoming;
    } else {
      const incomingIds = new Set(incoming.map((detail) => detail.run?.runId).filter(Boolean));
      adminTranscriptRuns.value = [
        ...incoming,
        ...adminTranscriptRuns.value.filter((detail) => !incomingIds.has(detail.run?.runId))
      ];
    }
    adminTranscriptHasMore.value = Boolean(result.pagination?.hasMore);
    adminTranscriptCursor.value = result.pagination?.nextCursor ?? null;
    await nextTick();
    const nextScroller = adminTranscriptScroll.value;
    if (nextScroller) {
      if (replace) nextScroller.scrollTop = nextScroller.scrollHeight;
      else nextScroller.scrollTop = previousTop + Math.max(0, nextScroller.scrollHeight - previousHeight);
    }
  } catch (err) {
    adminTranscriptError.value = err.message;
  } finally {
    if (replace) adminTranscriptLoading.value = false;
    else adminTranscriptLoadingMore.value = false;
  }
}

async function loadEarlierAdminTranscript() {
  if (!adminTranscriptHasMore.value || !adminTranscriptCursor.value) return;
  await loadAdminTranscriptPage(adminTranscriptConversation.value);
}

async function retryAdminTranscript() {
  await loadAdminTranscriptPage(adminTranscriptConversation.value, { replace: adminTranscriptRuns.value.length === 0 });
}

function beginAdminTranscriptDrag(event) {
  const scroller = event.currentTarget;
  if (!scroller || adminTranscriptInteractiveTarget(event.target)) return;
  if (event.pointerType === "touch") return;
  if (typeof event.button === "number" && event.button !== 0) return;
  if (scroller.scrollHeight <= scroller.clientHeight) return;
  adminTranscriptDrag.active = true;
  adminTranscriptDrag.moved = false;
  adminTranscriptDrag.pointerId = event.pointerId;
  adminTranscriptDrag.scroller = scroller;
  adminTranscriptDrag.startX = event.clientX;
  adminTranscriptDrag.startY = event.clientY;
  adminTranscriptDrag.scrollTop = scroller.scrollTop;
  scroller.classList.add("drag-scroll-active");
  scroller.setPointerCapture?.(event.pointerId);
  addAdminTranscriptDragListeners();
}

function moveAdminTranscriptDrag(event) {
  if (!adminTranscriptDrag.active || event.pointerId !== adminTranscriptDrag.pointerId) return;
  const scroller = adminTranscriptDrag.scroller ?? adminTranscriptScroll.value;
  if (!scroller) return;
  const deltaX = event.clientX - adminTranscriptDrag.startX;
  const deltaY = event.clientY - adminTranscriptDrag.startY;
  if (!adminTranscriptDrag.moved && Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 4) return;
  adminTranscriptDrag.moved = true;
  scroller.scrollTop = adminTranscriptDrag.scrollTop - deltaY;
  event.preventDefault();
}

function endAdminTranscriptDrag(event) {
  if (!adminTranscriptDrag.active) return;
  if (event.pointerId !== undefined && event.pointerId !== adminTranscriptDrag.pointerId) return;
  const scroller = adminTranscriptDrag.scroller ?? adminTranscriptScroll.value;
  const pointerId = adminTranscriptDrag.pointerId;
  const moved = adminTranscriptDrag.moved;
  adminTranscriptDrag.active = false;
  adminTranscriptDrag.moved = false;
  adminTranscriptDrag.pointerId = null;
  adminTranscriptDrag.scroller = null;
  removeAdminTranscriptDragListeners();
  scroller?.releasePointerCapture?.(pointerId);
  scroller?.classList.remove("drag-scroll-active");
  if (moved) {
    event.preventDefault?.();
  }
}

function adminTranscriptInteractiveTarget(target) {
  return Boolean(target?.closest?.("button,input,textarea,select,label,[role='button'],[contenteditable='true']"));
}

function addAdminTranscriptDragListeners() {
  window.addEventListener("pointermove", moveAdminTranscriptDrag, { passive: false });
  window.addEventListener("pointerup", endAdminTranscriptDrag, { passive: false });
  window.addEventListener("pointercancel", endAdminTranscriptDrag, { passive: false });
  window.addEventListener("blur", endAdminTranscriptDrag);
}

function removeAdminTranscriptDragListeners() {
  window.removeEventListener("pointermove", moveAdminTranscriptDrag);
  window.removeEventListener("pointerup", endAdminTranscriptDrag);
  window.removeEventListener("pointercancel", endAdminTranscriptDrag);
  window.removeEventListener("blur", endAdminTranscriptDrag);
}

async function refreshWorkspace() {
  if (!auth.value) return;
  loadingWorkspace.value = true;
  try {
    const [hostResult, conversationResult] = await Promise.all([
      api.get("/api/hosts"),
      api.get("/api/conversations")
    ]);
    hosts.value = hostResult.hosts;
    conversations.value = [...conversationResult.conversations].sort((a, b) => Date.parse(b.updatedAt ?? b.createdAt) - Date.parse(a.updatedAt ?? a.createdAt));

    const selectedStillExists = projectlessConversations.value.some((conversation) => conversation.conversationId === selectedConversationId.value);
    if (!selectedStillExists) {
      selectedConversationId.value = projectlessConversations.value[0]?.conversationId ?? "";
    }
    if (isDraftConversationId(selectedConversationId.value)) {
      runDetails.value = [];
      loadedConversationId.value = selectedConversationId.value;
    } else if (selectedConversationId.value) {
      await loadConversation(selectedConversationId.value);
    } else {
      runDetails.value = [];
      loadedConversationId.value = "";
    }
    error.value = "";
  } catch (err) {
    error.value = err.message;
  } finally {
    loadingWorkspace.value = false;
  }
}

async function selectConversation(conversation) {
  adminMode.value = false;
  if (isDraftConversation(conversation)) {
    selectedConversationId.value = conversation.conversationId;
    loadedConversationId.value = conversation.conversationId;
    runDetails.value = [];
    closeNavigationForMobile();
    scrollToBottom();
    return;
  }
  clearDraftConversation();
  if (
    conversation.conversationId === selectedConversationId.value &&
    loadedConversationId.value === conversation.conversationId
  ) {
    closeNavigationForMobile();
    return;
  }
  selectedConversationId.value = conversation.conversationId;
  closeNavigationForMobile();
  lockingConversationLoad.value = true;
  try {
    await Promise.all([
      loadConversation(conversation.conversationId),
      wait(320)
    ]);
  } finally {
    lockingConversationLoad.value = false;
  }
}

async function loadConversation(conversationId) {
  if (!conversationId) {
    runDetails.value = [];
    loadedConversationId.value = "";
    return;
  }
  if (isDraftConversationId(conversationId)) {
    runDetails.value = [];
    loadedConversationId.value = conversationId;
    loadingConversation.value = false;
    return;
  }
  loadingConversation.value = true;
  try {
    const result = await api.get(`/api/conversations/${conversationId}/transcript`);
    upsertConversation(result.conversation);
    selectedConversationId.value = result.conversation.conversationId;
    runDetails.value = result.runs;
    loadedConversationId.value = result.conversation.conversationId;
    error.value = "";
    scrollToBottom();
  } catch (err) {
    error.value = err.message;
  } finally {
    loadingConversation.value = false;
  }
}

function startDraftConversation() {
  adminMode.value = false;
  const host = selectedHost.value ?? hosts.value[0] ?? null;
  const now = new Date().toISOString();
  draftConversation.value = {
    conversationId: DRAFT_CONVERSATION_ID,
    tenantId: auth.value?.user?.tenantId ?? "single",
    hostId: host?.hostId ?? null,
    projectId: null,
    title: "新会话",
    createdAt: now,
    updatedAt: now,
    codexThreadId: null,
    activeRun: null,
    draft: true
  };
  selectedConversationId.value = DRAFT_CONVERSATION_ID;
  loadedConversationId.value = DRAFT_CONVERSATION_ID;
  runDetails.value = [];
  error.value = "";
  closeNavigationForMobile();
  scrollToBottom();
  return draftConversation.value;
}

async function createConversationOnServer(seedConversation = null) {
  const host = seedConversation?.hostId
    ? hosts.value.find((item) => item.hostId === seedConversation.hostId) ?? null
    : selectedHost.value ?? hosts.value[0] ?? null;
  if (!host) {
    throw new Error("未连接 Mac 监听端。");
  }
  const result = await api.post("/api/conversations", { hostId: host.hostId, title: "单独会话" });
  upsertConversation(result.conversation);
  return result.conversation;
}

async function ensureRealConversation(conversation) {
  if (conversation && !isDraftConversation(conversation)) {
    return conversation;
  }
  const created = await createConversationOnServer(conversation);
  if (!conversation || isDraftConversation(conversation) || isDraftConversationId(selectedConversationId.value)) {
    clearDraftConversation();
    selectedConversationId.value = created.conversationId;
    loadedConversationId.value = created.conversationId;
    runDetails.value = [];
  }
  return created;
}

async function submitTurn() {
  const rawPrompt = message.value.trim() || (pendingAttachments.value.length ? "请查看附件。" : "");
  const slashCommand = resolveSlashCommand(rawPrompt);
  if (slashCommand?.command.local) {
    if (slashCommand.command.name === "clear") {
      message.value = "";
      pendingAttachments.value = [];
    }
    return;
  }
  const displayPrompt = slashCommand?.displayPrompt ?? rawPrompt;
  const prompt = appendFileReferenceContext(slashCommand?.prompt ?? rawPrompt, fileMentions.value);
  if (!prompt || composerLocked.value) return;

  const turnDraft = {
    rawPrompt,
    displayPrompt,
    prompt,
    mode: slashCommand?.mode ?? null,
    reviewTarget: slashCommand?.reviewTarget ?? null,
    attachments: pendingAttachments.value.map((attachment) => ({ ...attachment }))
  };

  let conversation = selectedConversation.value;
  if (conversation && !isDraftConversation(conversation) && runIsActive.value) {
    enqueueQueuedTurn(conversation, turnDraft);
    recordComposerHistory(rawPrompt);
    message.value = "";
    pendingAttachments.value = [];
    scrollToBottom();
    return;
  }

  busy.value = true;
  error.value = "";
  try {
    conversation = await ensureRealConversation(conversation);
    const { result, uploadResult } = await submitTurnToApi(conversation, turnDraft);
    recordComposerHistory(rawPrompt);
    appendOptimisticRun(result, turnDraft.displayPrompt, uploadResult.attachments);
    message.value = "";
    pendingAttachments.value = [];
    scrollToBottom();
  } catch (err) {
    error.value = err.message;
  } finally {
    busy.value = false;
  }
}

async function submitTurnToApi(conversation, turnDraft) {
  const uploadResult = turnDraft.attachments.length
    ? await uploadAttachments(conversation.conversationId, turnDraft.attachments)
    : { jobId: null, attachments: [] };
  const result = await api.post("/api/turns/submit", {
    conversationId: conversation.conversationId,
    message: turnDraft.prompt,
    ...(turnDraft.mode ? { mode: turnDraft.mode } : {}),
    ...(turnDraft.reviewTarget ? { reviewTarget: turnDraft.reviewTarget } : {}),
    ...(uploadResult.jobId ? { jobId: uploadResult.jobId } : {}),
    ...(uploadResult.attachments.length ? { attachments: uploadResult.attachments } : {}),
    idempotencyKey: crypto.randomUUID()
  });
  if (result.conversation) {
    upsertConversation(result.conversation);
  }
  return { result, uploadResult };
}

function appendOptimisticRun(result, optimisticPrompt, optimisticAttachments = []) {
  runDetails.value = [
    ...runDetails.value,
    {
      run: result.run,
      turn: result.turn,
      events: [],
      approvals: [],
      usage: [],
      optimisticPrompt,
      optimisticAttachments
    }
  ];
}

async function decideApproval(item, decision) {
  if (runControlBusy.value) return;
  runControlBusy.value = true;
  try {
    error.value = "";
    await api.post(`/api/runs/${item.runId}/approvals/${item.approvalId}/decision`, { decision });
    await refreshRunDetail(item.runId);
  } catch (err) {
    error.value = err.message;
  } finally {
    runControlBusy.value = false;
  }
}

async function interruptActiveRun() {
  const runId = activeRun.value?.runId;
  if (!runId || runControlBusy.value) return;
  runControlBusy.value = true;
  try {
    error.value = "";
    const result = await api.post(`/api/runs/${runId}/interrupt`, {});
    if (result.run) {
      patchRun(result.run);
    }
    await refreshRunDetail(runId);
  } catch (err) {
    error.value = err.message;
  } finally {
    runControlBusy.value = false;
  }
}

async function resumeActiveRun() {
  const runId = activeRun.value?.runId;
  if (!runId || !canResumeRun.value || runControlBusy.value) return;
  runControlBusy.value = true;
  try {
    error.value = "";
    const result = await api.post(`/api/runs/${runId}/resume`, {});
    if (result.conversation) {
      upsertConversation(result.conversation);
    }
    runDetails.value = [
      ...runDetails.value,
      {
        run: result.run,
        turn: result.turn ?? activeDetail.value?.turn ?? null,
        events: [],
        approvals: [],
        usage: []
      }
    ];
    scrollToBottom();
  } catch (err) {
    error.value = err.message;
  } finally {
    runControlBusy.value = false;
  }
}

function handleAttachmentInput(event) {
  const files = Array.from(event.target.files ?? []);
  const existing = new Set(pendingAttachments.value.map((attachment) => `${attachment.name}:${attachment.size}`));
  const next = files
    .filter((file) => !existing.has(`${file.name}:${file.size}`))
    .map((file) => ({
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      contentType: file.type || "application/octet-stream"
    }));
  pendingAttachments.value = [...pendingAttachments.value, ...next];
  event.target.value = "";
}

function removeAttachment(id) {
  pendingAttachments.value = pendingAttachments.value.filter((attachment) => attachment.id !== id);
}

function enqueueQueuedTurn(conversation, turnDraft) {
  const persistable = turnDraft.attachments.length === 0;
  queuedTurns.value = [
    ...queuedTurns.value,
    {
      ...turnDraft,
      id: crypto.randomUUID(),
      conversationId: conversation.conversationId,
      persistable,
      queuedAt: new Date().toISOString()
    }
  ];
}

function removeQueuedTurn(queueId) {
  queuedTurns.value = queuedTurns.value.filter((turn) => turn.id !== queueId);
}

function editQueuedTurn(queueId) {
  const queuedTurn = queuedTurns.value.find((turn) => turn.id === queueId);
  if (!queuedTurn) {
    return;
  }
  queuedTurns.value = queuedTurns.value.filter((turn) => turn.id !== queueId);
  message.value = queuedTurn.rawPrompt;
  pendingAttachments.value = queuedTurn.attachments.map((attachment) => ({ ...attachment }));
  nextTick(() => {
    document.querySelector(".composer-input textarea")?.focus();
  });
}

function queuedTurnToTranscriptItem(turn) {
  return {
    id: `queued:${turn.id}`,
    queueId: turn.id,
    kind: "message",
    role: "user",
    text: turn.displayPrompt,
    attachments: turn.attachments.map((attachment) => ({
      fileId: attachment.id,
      name: attachment.name,
      size: attachment.size
    })),
    ephemeral: turn.persistable === false,
    queued: true,
    runId: "queued"
  };
}

function canDrainQueuedTurns() {
  if (
    queueDrainBusy ||
    runIsActive.value ||
    !selectedConversationId.value ||
    isDraftConversationId(selectedConversationId.value) ||
    loadedConversationId.value !== selectedConversationId.value
  ) {
    return false;
  }
  const status = activeRun.value?.status ?? "";
  return !status || status === RunStatus.SUCCEEDED;
}

async function drainQueuedTurns() {
  if (!canDrainQueuedTurns()) {
    return;
  }
  const next = queuedTurns.value.find((turn) => turn.conversationId === selectedConversationId.value);
  if (!next) {
    return;
  }
  const conversation = selectedConversation.value;
  if (!conversation) {
    return;
  }
  queueDrainBusy = true;
  busy.value = true;
  error.value = "";
  try {
    const { result, uploadResult } = await submitTurnToApi(conversation, next);
    queuedTurns.value = queuedTurns.value.filter((turn) => turn.id !== next.id);
    appendOptimisticRun(result, next.displayPrompt, uploadResult.attachments);
    scrollToBottom();
  } finally {
    queueDrainBusy = false;
    busy.value = false;
  }
}

async function uploadAttachments(conversationId, attachments) {
  const prepared = await api.post("/api/turns/prepare-upload", {
    conversationId,
    attachments: attachments.map((attachment) => ({
      name: attachment.name,
      size: attachment.size,
      contentType: attachment.contentType
    }))
  });
  const uploaded = [];
  for (const [index, preparedAttachment] of prepared.attachments.entries()) {
    const pending = attachments[index];
    const bytes = await pending.file.arrayBuffer();
    const sha256 = await sha256ArrayBuffer(bytes);
    await uploadPreparedAttachment(preparedAttachment, pending, bytes, sha256);
    uploaded.push({
      fileId: preparedAttachment.fileId,
      name: pending.name,
      size: pending.size,
      objectKey: preparedAttachment.objectKey,
      sha256,
      contentType: pending.contentType
    });
  }
  return { jobId: prepared.jobId, attachments: uploaded };
}

async function uploadPreparedAttachment(preparedAttachment, pending, bytes, sha256) {
  const upload = preparedAttachment.upload;
  if (canDirectUpload(upload)) {
    try {
      const response = await fetch(upload.url, {
        method: upload.method ?? "PUT",
        headers: {
          "content-type": pending.contentType || "application/octet-stream",
          "x-cos-meta-sha256": sha256
        },
        body: bytes
      });
      if (!response.ok) {
        throw new Error(`direct upload failed: HTTP ${response.status}`);
      }
      return;
    } catch (err) {
      console.warn("Direct COS upload failed; falling back to API upload.", err);
    }
  }
  await api.post("/api/turns/upload-object", {
    objectKey: preparedAttachment.objectKey,
    name: pending.name,
    contentType: pending.contentType,
    sha256,
    dataBase64: arrayBufferToBase64(bytes)
  });
}

function canDirectUpload(upload) {
  return Boolean(upload?.url && /^https?:\/\//i.test(upload.url) && String(upload.method ?? "PUT").toUpperCase() === "PUT");
}

function messageCopyText(item) {
  return String(item?.text ?? "").trim();
}

async function copyMessageText(item) {
  const text = messageCopyText(item);
  if (!text) return;
  await copyPlainText(text, item.id);
}

async function copyPlainText(text, copyId) {
  try {
    error.value = "";
    await writeClipboardText(text);
    copiedMessageId.value = copyId;
    if (copyResetTimer) {
      clearTimeout(copyResetTimer);
    }
    copyResetTimer = setTimeout(() => {
      if (copiedMessageId.value === copyId) {
        copiedMessageId.value = "";
      }
    }, 1400);
  } catch (err) {
    error.value = err.message || "复制失败，请手动选择文本复制。";
  }
}

async function writeClipboardText(text) {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "-9999px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) {
    throw new Error("复制失败，请手动选择文本复制。");
  }
}

async function downloadArtifact(item) {
  if (!item?.runId || !item.objectKey || downloadingArtifactId.value) {
    return;
  }
  downloadingArtifactId.value = item.id;
  try {
    error.value = "";
    const result = await api.get(`/api/runs/${encodeURIComponent(item.runId)}/download-url?objectKey=${encodeURIComponent(item.objectKey)}`);
    const url = result.download?.url;
    if (!url) {
      throw new Error("未获取到下载链接。");
    }
    if (!/^https?:\/\//i.test(url)) {
      throw new Error("当前对象存储返回本地调试地址，浏览器无法直接下载；上线 COS 后会生成可下载链接。");
    }
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noreferrer";
    if (item.name) {
      link.download = item.name;
    }
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    error.value = err.message;
  } finally {
    downloadingArtifactId.value = "";
  }
}

async function refreshRunDetail(runId) {
  const [runResult, eventResult, approvalResult, usageResult] = await Promise.all([
    api.get(`/api/runs/${runId}`),
    api.get(`/api/runs/${runId}/events`),
    api.get(`/api/runs/${runId}/approvals`),
    api.get(`/api/usage/runs/${runId}`)
  ]);
  if (runResult.conversation) {
    upsertConversation(runResult.conversation);
  }
  const index = runDetails.value.findIndex((detail) => detail.run.runId === runId);
  const previous = index >= 0 ? runDetails.value[index] : {};
  const next = {
    ...previous,
    run: runResult.run,
    manifest: runResult.manifest ?? previous.manifest ?? null,
    events: eventResult.events,
    approvals: approvalResult.approvals,
    usage: usageResult.usage
  };
  if (index >= 0) {
    runDetails.value = runDetails.value.map((detail, itemIndex) => itemIndex === index ? next : detail);
  } else {
    runDetails.value = [...runDetails.value, next];
  }
  if (TERMINAL_STATUSES.has(runResult.run.status) && selectedConversationId.value) {
    terminalReloadedRunIds.add(runId);
    await loadConversation(selectedConversationId.value);
  }
}

async function startRunStream(runId) {
  const controller = new AbortController();
  streamAbortController = controller;
  try {
    const sinceSeq = latestRunEventSeq(runId);
    const response = await requestStream(`/api/runs/${runId}/stream?sinceSeq=${encodeURIComponent(String(sinceSeq))}`, auth, controller.signal);
    await readSse(response, async (eventName, payload) => {
      if (controller.signal.aborted) return;
      if (eventName === "snapshot") {
        applyRunSnapshot(runId, payload);
        if (payload.run && TERMINAL_STATUSES.has(payload.run.status) && selectedConversationId.value && !terminalReloadedRunIds.has(runId)) {
          terminalReloadedRunIds.add(runId);
          await loadConversation(selectedConversationId.value);
        }
      }
      if (eventName === "done" && selectedConversationId.value && !terminalReloadedRunIds.has(runId)) {
        terminalReloadedRunIds.add(runId);
        await loadConversation(selectedConversationId.value);
      }
    });
  } catch (err) {
    if (!controller.signal.aborted) {
      error.value = err.message;
    }
  }
}

function applyRunSnapshot(runId, payload) {
  if (!payload?.run) return;
  const index = runDetails.value.findIndex((detail) => detail.run?.runId === runId);
  const previous = index >= 0 ? runDetails.value[index] : {};
  const previousEvents = previous.events ?? [];
  const next = {
    ...previous,
    run: payload.run,
    manifest: payload.manifest ?? previous.manifest ?? null,
    events: payload.events
      ? mergeEventsBySeq(previousEvents, payload.events)
      : previousEvents,
    approvals: payload.approvals ?? previous.approvals ?? [],
    usage: payload.usage ?? previous.usage ?? []
  };
  if (index >= 0) {
    runDetails.value = runDetails.value.map((detail, itemIndex) => itemIndex === index ? next : detail);
  } else {
    runDetails.value = [...runDetails.value, next];
  }
}

function latestRunEventSeq(runId) {
  const detail = runDetails.value.find((item) => item.run?.runId === runId);
  return (detail?.events ?? []).reduce((max, event) => Math.max(max, Number(event.seq ?? 0)), 0);
}

function mergeEventsBySeq(existing, incoming) {
  const bySeq = new Map();
  for (const event of existing ?? []) {
    bySeq.set(Number(event.seq ?? 0), event);
  }
  for (const event of incoming ?? []) {
    bySeq.set(Number(event.seq ?? 0), event);
  }
  return [...bySeq.values()].sort((a, b) => Number(a.seq ?? 0) - Number(b.seq ?? 0));
}

async function logout() {
  stopRunWatch();
  await api.post("/api/auth/logout", {}).catch(() => undefined);
  setAuth(null);
  conversations.value = [];
  hosts.value = [];
  runDetails.value = [];
  draftConversation.value = null;
  selectedConversationId.value = "";
  loadedConversationId.value = "";
  pendingAttachments.value = [];
  queuedTurns.value = [];
  resetAdminState();
}

function resetAdminState() {
  adminMode.value = false;
  adminError.value = "";
  adminOverview.value = null;
  adminUsers.value = [];
  adminConversations.value = [];
  adminInvitations.value = [];
  adminInviteNote.value = "";
  generatedInviteCode.value = "";
  adminTranscriptDialog.value = false;
  adminTranscriptLoading.value = false;
  adminTranscriptLoadingMore.value = false;
  adminTranscriptError.value = "";
  adminTranscriptHasMore.value = false;
  adminTranscriptCursor.value = null;
  adminTranscriptConversation.value = null;
  adminTranscriptRuns.value = [];
}

function upsertConversation(conversation) {
  conversations.value = [
    conversation,
    ...conversations.value.filter((item) => item.conversationId !== conversation.conversationId)
  ].sort((a, b) => Date.parse(b.updatedAt ?? b.createdAt) - Date.parse(a.updatedAt ?? a.createdAt));
}

function clearDraftConversation() {
  draftConversation.value = null;
}

function isDraftConversation(conversation) {
  return isDraftConversationId(conversation?.conversationId);
}

function isDraftConversationId(conversationId) {
  return String(conversationId ?? "") === DRAFT_CONVERSATION_ID;
}

function patchRun(run) {
  runDetails.value = runDetails.value.map((detail) => detail.run.runId === run.runId ? { ...detail, run } : detail);
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function stopRunWatch() {
  stopPolling();
  if (streamAbortController) {
    streamAbortController.abort();
    streamAbortController = null;
  }
}

function handleComposerKeydown(event) {
  if (slashCommandsOpen.value) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      slashCommandIndex.value = (slashCommandIndex.value + 1) % filteredSlashCommands.value.length;
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      slashCommandIndex.value = (slashCommandIndex.value - 1 + filteredSlashCommands.value.length) % filteredSlashCommands.value.length;
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      slashMenuDismissed.value = true;
      return;
    }
    if (event.key === "Enter" && !event.shiftKey && !event.metaKey && !event.ctrlKey && !event.altKey && !event.isComposing) {
      event.preventDefault();
      applySlashCommand(filteredSlashCommands.value[slashCommandIndex.value]);
      return;
    }
  }
  if (event.key === "Enter" && !event.shiftKey && !event.metaKey && !event.ctrlKey && !event.altKey && !event.isComposing) {
    event.preventDefault();
    submitTurn();
    return;
  }
  if (event.key === "ArrowUp" && shouldRecallHistory(event, -1)) {
    event.preventDefault();
    recallComposerHistory(-1);
    return;
  }
  if (event.key === "ArrowDown" && shouldRecallHistory(event, 1)) {
    event.preventDefault();
    recallComposerHistory(1);
  }
}

function shouldRecallHistory(event, direction) {
  if (pendingAttachments.value.length) return false;
  const target = event.target;
  const value = String(target?.value ?? message.value ?? "");
  const position = Number(target?.selectionStart ?? 0);
  if (direction < 0) {
    return position === 0 || value.trim() === "";
  }
  return historyCursor.value >= 0 && position === value.length;
}

function recordComposerHistory(value) {
  const text = String(value ?? "").trim();
  if (!text || text === composerHistory.value[0]) return;
  composerHistory.value = [text, ...composerHistory.value.filter((item) => item !== text)].slice(0, 40);
  historyCursor.value = -1;
  historyDraft.value = "";
}

function recallComposerHistory(direction) {
  const history = composerHistory.value;
  if (!history.length) return;
  if (direction < 0) {
    if (historyCursor.value < 0) {
      historyDraft.value = message.value;
    }
    historyCursor.value = Math.min(historyCursor.value + 1, history.length - 1);
    message.value = history[historyCursor.value] ?? message.value;
    return;
  }
  if (historyCursor.value <= 0) {
    historyCursor.value = -1;
    message.value = historyDraft.value;
    historyDraft.value = "";
    return;
  }
  historyCursor.value -= 1;
  message.value = history[historyCursor.value] ?? message.value;
}

function extractFileMentions(text, projectRoot) {
  const mentions = [];
  const seen = new Set();
  const pattern = /(^|\s)@([^\s@]+)/g;
  let match;
  while ((match = pattern.exec(String(text ?? "")))) {
    const raw = match[2].replace(/[，。；;,.!?]+$/g, "");
    if (!looksLikeFileReference(raw)) continue;
    const token = `@${raw}`;
    if (seen.has(token)) continue;
    seen.add(token);
    mentions.push({
      token,
      label: raw.length > 34 ? `...${raw.slice(-31)}` : raw,
      path: resolveMentionPath(raw, projectRoot)
    });
  }
  return mentions;
}

function looksLikeFileReference(value) {
  const text = String(value ?? "");
  return Boolean(text && (
    text.startsWith("/") ||
    text.startsWith("./") ||
    text.startsWith("../") ||
    text.startsWith("~/") ||
    text.includes("/") ||
    /\.[A-Za-z0-9]{1,8}$/.test(text)
  ));
}

function resolveMentionPath(value, projectRoot) {
  const text = String(value ?? "");
  if (text.startsWith("/") || text.startsWith("~/") || text.startsWith("../") || text.startsWith("./")) {
    return text;
  }
  const root = String(projectRoot ?? "").replace(/\/+$/, "");
  return root ? `${root}/${text}` : text;
}

function appendFileReferenceContext(prompt, mentions) {
  const text = String(prompt ?? "").trim();
  if (!mentions.length) return text;
  const lines = mentions.map((mention) => `- ${mention.token}: ${mention.path}`);
  return `${text}\n\nSkybridge file references:\n${lines.join("\n")}`;
}

function resolveSlashCommand(rawPrompt) {
  const match = String(rawPrompt ?? "").trim().match(/^\/([A-Za-z]+)(?:\s+([\s\S]*))?$/);
  if (!match) return null;
  const command = SLASH_COMMANDS.find((item) => item.name === match[1].toLowerCase());
  if (!command) return null;
  const body = String(match[2] ?? "").trim();
  if (command.local) {
    return { command, displayPrompt: rawPrompt, prompt: body };
  }
  if (command.mode === "review") {
    return {
      command,
      displayPrompt: body || "/review",
      prompt: body || "Review uncommitted changes.",
      mode: "review",
      reviewTarget: body
        ? { type: "custom", instructions: body }
        : { type: "uncommittedChanges" }
    };
  }
  const prompt = body || command.description;
  return {
    command,
    displayPrompt: body || `/${command.name}`,
    prompt: `${prompt}\n\nSkybridge slash command:\n- /${command.name}: ${command.instruction}`
  };
}

function applySlashCommand(command) {
  if (!command) return;
  message.value = `/${command.name} `;
  slashMenuDismissed.value = true;
  nextTick(() => {
    document.querySelector(".composer-input textarea")?.focus();
  });
}

function removeFileMention(token) {
  const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  message.value = message.value
    .replace(new RegExp(`(^|\\s)${escaped}(?=\\s|$)`, "g"), "$1")
    .replace(/[ \t]{2,}/g, " ")
    .trimStart();
}

function scrollToBottom(options = {}) {
  const force = options.force ?? true;
  nextTick(() => {
    const element = conversationScroll.value;
    if (!element) return;
    if (!force && !shouldAutoScroll.value) return;
    element.scrollTop = element.scrollHeight;
    shouldAutoScroll.value = true;
  });
}

function handleConversationScroll() {
  shouldAutoScroll.value = isConversationNearBottom();
}

function isConversationNearBottom() {
  const element = conversationScroll.value;
  if (!element) return true;
  return element.scrollHeight - element.scrollTop - element.clientHeight < 96;
}

function closeNavigationForMobile() {
  if (window.matchMedia("(max-width: 760px) and (orientation: portrait)").matches) {
    navigationDrawer.value = false;
  }
}

function updateResponsiveDrawers() {
  navigationDrawer.value = window.innerWidth >= 760;
}

function commandTitle(item) {
  return item.command?.trim() || "命令执行";
}

function commandStatusLabel(status) {
  const labels = {
    in_progress: "运行中",
    inProgress: "运行中",
    completed: "已完成",
    failed: "失败"
  };
  return labels[status] ?? statusTitle(status);
}

function statusTone(status) {
  if (status === "failed") return "negative";
  if (status === "completed") return "teal";
  return "primary";
}

function compactPath(value) {
  const path = String(value ?? "").replaceAll("\\", "/").trim();
  if (!path) return "";
  const parts = path.split("/").filter(Boolean);
  if (parts.length <= 3) return path;
  return `.../${parts.slice(-3).join("/")}`;
}

function fileChangeSummary(item) {
  const count = item.changes?.length ?? 0;
  if (!count) return "Codex 更新了文件";
  return `${count} 个文件 · ${commandStatusLabel(item.status)}`;
}

function fileChangeIcon(kind) {
  if (kind === "add") return "add";
  if (kind === "delete") return "remove";
  return "edit";
}

function fileChangeKindLabel(kind) {
  const labels = {
    add: "新增",
    delete: "删除",
    update: "修改"
  };
  return labels[kind] ?? "更新";
}

function diffSummary(item) {
  const count = item.files?.length ?? 0;
  if (!count) return "文件差异";
  return `${count} 个文件 · +${item.additions ?? 0} -${item.deletions ?? 0}`;
}

function diffSubtitle(item) {
  const first = item.files?.[0]?.path;
  if (!first) return "Codex 生成了 diff";
  if ((item.files?.length ?? 0) === 1) return compactPath(first);
  return `${compactPath(first)} 等`;
}

function diffPreviewLines(item) {
  const lines = item.lines ?? String(item.diff ?? "").split(/\r?\n/);
  return lines.slice(0, 220);
}

function diffLineClass(line) {
  const text = String(line ?? "");
  if (text.startsWith("+") && !text.startsWith("+++")) return "added";
  if (text.startsWith("-") && !text.startsWith("---")) return "removed";
  if (text.startsWith("@@")) return "hunk";
  if (text.startsWith("diff --git") || text.startsWith("index ") || text.startsWith("---") || text.startsWith("+++")) return "meta";
  return "";
}

function toolTitle(item) {
  const server = item.server ? `${item.server} / ` : "";
  return `${server}${item.tool ?? "工具调用"}`;
}

function toolSubtitle(item) {
  const text = formatToolPayload(item.input).replace(/\s+/g, " ").trim();
  if (!text) return "";
  return text.length > 96 ? `${text.slice(0, 96).trim()}...` : text;
}

function toolIcon(item) {
  if (item.toolType === "mcpToolCall") return "extension";
  if (item.toolType === "dynamicToolCall") return "bolt";
  if (item.toolType === "collabAgentToolCall") return "hub";
  return "api";
}

function toolStatusLabel(status) {
  if (status === "completed") return "工具调用已完成";
  if (status === "failed") return "工具调用失败";
  return "工具调用中";
}

function formatToolPayload(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function assetIcon(item) {
  if (item.assetType === "artifact") return "description";
  if (item.assetType === "imageGeneration") return "image";
  if (item.assetType === "imageView") return "visibility";
  return "inventory_2";
}

function assetTitle(item) {
  if (item.assetType === "artifact") return item.name || "生成文件";
  return item.label || (item.assetType === "imageGeneration" ? "生成图片" : "产物");
}

function assetMeta(item) {
  const parts = [];
  const size = Number(item.size ?? 0);
  if (Number.isFinite(size) && size > 0) {
    parts.push(formatBytes(size));
  }
  parts.push(assetTypeLabel(item));
  return parts.filter(Boolean).join(" · ") || "Codex 生成的产物";
}

function assetContext(item) {
  if (item.url) return item.url;
  const path = String(item.path ?? "").replaceAll("\\", "/").trim();
  if (!path) return "";
  const title = assetTitle(item).trim();
  const parts = path.split("/").filter(Boolean);
  const filename = parts.at(-1) ?? path;
  if (filename === title) {
    const folder = parts.slice(0, -1).join("/");
    return folder ? `位置：${compactPath(folder)}` : "";
  }
  return compactPath(path);
}

function assetBadge(item) {
  const extension = extensionFromName(assetTitle(item)) || extensionFromName(item.path) || extensionFromContentType(item.contentType);
  return extension ? extension.toUpperCase() : "";
}

function assetTypeLabel(item) {
  const contentType = String(item.contentType ?? "").toLowerCase();
  const extension = (extensionFromName(assetTitle(item)) || extensionFromName(item.path) || extensionFromContentType(contentType)).toLowerCase();
  if (contentType.includes("word") || extension === "docx" || extension === "doc") return "Word 文档";
  if (contentType.includes("pdf") || extension === "pdf") return "PDF 文档";
  if (contentType.includes("spreadsheet") || ["xlsx", "xls", "csv"].includes(extension)) return "表格文件";
  if (contentType.includes("presentation") || ["pptx", "ppt"].includes(extension)) return "演示文稿";
  if (contentType.startsWith("image/") || ["png", "jpg", "jpeg", "webp", "gif", "svg"].includes(extension)) return item.assetType === "imageGeneration" ? "生成图片" : "图片文件";
  if (["md", "txt", "json", "html"].includes(extension)) return "文本文件";
  if (["zip", "gz", "tar"].includes(extension)) return "压缩文件";
  if (item.assetType === "artifact") return "生成文件";
  if (item.assetType === "imageGeneration") return "生成图片";
  if (item.assetType === "imageView") return "图片预览";
  return "Codex 产物";
}

function extensionFromName(value) {
  const filename = String(value ?? "").replaceAll("\\", "/").split("/").pop()?.trim() ?? "";
  const index = filename.lastIndexOf(".");
  if (index <= 0 || index === filename.length - 1) return "";
  return filename.slice(index + 1).toLowerCase();
}

function extensionFromContentType(value) {
  const contentType = String(value ?? "").toLowerCase();
  if (contentType.includes("word")) return "docx";
  if (contentType.includes("pdf")) return "pdf";
  if (contentType.includes("spreadsheet")) return "xlsx";
  if (contentType.includes("presentation")) return "pptx";
  if (contentType.includes("png")) return "png";
  if (contentType.includes("jpeg")) return "jpg";
  if (contentType.includes("webp")) return "webp";
  return "";
}

function hookStatusLabel(status) {
  const labels = {
    running: "运行中",
    completed: "已完成",
    failed: "失败",
    blocked: "已阻止",
    stopped: "已停止"
  };
  return labels[status] ?? commandStatusLabel(status);
}

function hookStatusTone(status) {
  if (status === "failed" || status === "blocked") return "negative";
  if (status === "completed") return "teal";
  if (status === "stopped") return "warning";
  return "primary";
}

function hookSubtitle(item) {
  const parts = [
    hookSourceLabel(item.source),
    hookHandlerLabel(item.handlerType),
    item.durationMs ? `${item.durationMs} ms` : ""
  ].filter(Boolean);
  return parts.join(" · ");
}

function hookSourceLabel(source) {
  const labels = {
    system: "系统",
    user: "用户",
    project: "工作区",
    mdm: "设备管理",
    sessionFlags: "会话配置",
    plugin: "插件",
    cloudRequirements: "云端要求",
    cloudManagedConfig: "云端配置",
    legacyManagedConfigFile: "托管配置",
    legacyManagedConfigMdm: "托管设备配置"
  };
  return labels[source] ?? "";
}

function hookHandlerLabel(type) {
  const labels = {
    command: "命令",
    prompt: "提示",
    agent: "代理"
  };
  return labels[type] ?? "";
}

function subAgentIcon(status) {
  if (status === "interrupted") return "pause_circle";
  if (status === "interacted") return "forum";
  return "account_tree";
}

function subAgentTitle(status) {
  const labels = {
    started: "子任务已启动",
    interacted: "子任务有新交互",
    interrupted: "子任务已中断"
  };
  return labels[status] ?? "子任务活动";
}

function subAgentSubtitle(item) {
  const path = compactPath(item.agentPath);
  if (path && item.agentThreadId) return `${path} · ${item.agentThreadId}`;
  if (path) return path;
  return item.agentThreadId || "Codex 子任务";
}

function autoReviewIcon(status) {
  if (status === "approved" || status === "completed") return "verified";
  if (status === "denied" || status === "timedOut" || status === "aborted") return "gpp_bad";
  return "policy";
}

function autoReviewTitle(status) {
  const labels = {
    inProgress: "自动审批审查中",
    approved: "自动审批已通过",
    denied: "自动审批已拒绝",
    timedOut: "自动审批超时",
    aborted: "自动审批已停止",
    completed: "自动审批已完成"
  };
  return labels[status] ?? "自动审批审查";
}

function autoReviewSubtitle(item) {
  return item.actionLabel || "Codex 正在评估这个审批请求";
}

function autoReviewRiskLabel(value) {
  const labels = {
    low: "低风险",
    medium: "中风险",
    high: "高风险",
    critical: "严重风险"
  };
  return labels[value] ?? value;
}

function planStepIcon(status) {
  if (status === "completed") return "check_circle";
  if (status === "in_progress" || status === "inProgress") return "radio_button_checked";
  return "radio_button_unchecked";
}

function approvalSummary(approval) {
  return approval?.summary ?? approval?.reason ?? "Codex 请求执行需要确认的操作。";
}

function approvalStatusLabel(approval) {
  if (approval?.decision === "approved" || approval?.decision === "accept") return "已批准";
  if (approval?.decision === "denied" || approval?.decision === "cancel") return "已拒绝";
  return "已处理";
}

function activityExpanded(item) {
  const key = activityExpansionKey(item);
  if (!key) return false;
  const saved = activityExpansion.value[key];
  if (typeof saved === "boolean") return saved;
  return false;
}

function setActivityExpanded(item, expanded) {
  const key = activityExpansionKey(item);
  if (!key) return;
  const next = {
    ...activityExpansion.value,
    [key]: Boolean(expanded)
  };
  activityExpansion.value = pruneActivityExpansion(next);
}

function activityExpansionKey(item) {
  const conversationId = selectedConversationId.value || item?.runId || "global";
  const itemId = String(item?.id ?? "").trim();
  if (!conversationId || !itemId || item?.queued) return "";
  return `${conversationId}:${itemId}`;
}

function pruneActivityExpansion(value) {
  const entries = Object.entries(value ?? {});
  if (entries.length <= MAX_ACTIVITY_EXPANSION_ENTRIES) {
    return Object.fromEntries(entries);
  }
  return Object.fromEntries(entries.slice(-MAX_ACTIVITY_EXPANSION_ENTRIES));
}

function conversationTitle(conversation) {
  const title = conversation?.title?.trim();
  if (!title) return "新会话";
  if (title === "Projectless chat" || title === "无项目会话") return "单独会话";
  return title;
}

function conversationIsThinking(conversation) {
  if (!conversation?.conversationId) {
    return false;
  }
  const isSelected = conversation.conversationId === selectedConversationId.value;
  const status = isSelected ? activeRun.value?.status ?? conversation.activeRun?.status : conversation.activeRun?.status;
  return THINKING_RUN_STATUSES.has(status);
}

function displayPhone(value) {
  const text = String(value ?? "").trim();
  if (!text) return "-";
  return text.startsWith("+86") ? text.slice(3) : text;
}

function hostStatusLabel(status) {
  return HOST_STATUS_LABELS[status] ?? statusTitle(status);
}

function statusTitle(status) {
  if (!status) return "就绪";
  return STATUS_LABELS[status] ?? String(status).replaceAll("_", " ");
}

function invitationStatusLabel(status) {
  const labels = {
    pending: "待使用",
    used: "已核销",
    revoked: "已失效"
  };
  return labels[status] ?? statusTitle(status);
}

function adminTranscriptTitle(item) {
  if (item.kind === "message") {
    return item.role === "user" ? "用户" : "Codex";
  }
  if (item.kind === "webSearch") return "网页搜索";
  if (item.kind === "command") return "命令";
  if (item.kind === "tool") return "工具调用";
  if (item.kind === "asset") return "文件";
  if (item.kind === "approval") return "审批";
  return "活动";
}

function adminActivityLabel(item) {
  if (item.kind === "webSearch") return item.query ?? "网页搜索";
  if (item.kind === "command") return commandTitle(item);
  if (item.kind === "tool") return toolTitle(item);
  if (item.kind === "asset") return assetTitle(item);
  if (item.kind === "approval") return approvalSummary(item.approval);
  if (item.kind === "reasoning") return "Codex 正在思考";
  return item.text ?? item.title ?? statusTitle(item.status);
}

function recoveryActionText(status) {
  if (status === RunStatus.INTERRUPTED) return "已中断 · 可以继续这次会话";
  if (status === RunStatus.NEEDS_RESUME) return "等待继续 · 当前输出已保留";
  if (status === RunStatus.HOST_UNAVAILABLE) return "监听端不可用 · 恢复在线后可继续";
  if (status === RunStatus.EXPIRED) return "已过期 · 可以重新继续";
  if (status === RunStatus.CANCELLED) return "已取消 · 可以继续";
  if (status === RunStatus.FAILED) return "失败 · 可以继续";
  return `${statusTitle(status)} · 可继续`;
}

function formatBytes(value) {
  const size = Number(value ?? 0);
  if (!Number.isFinite(size) || size <= 0) return "0 B";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(size < 10 * 1024 ? 1 : 0)} KB`;
  return `${(size / 1024 / 1024).toFixed(size < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

async function sha256ArrayBuffer(buffer) {
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return btoa(binary);
}

function readQueuedTurns() {
  try {
    const parsed = JSON.parse(localStorage.getItem(QUEUED_TURNS_STORAGE_KEY) ?? "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizePersistedQueuedTurn)
      .filter(Boolean)
      .slice(0, MAX_PERSISTED_QUEUED_TURNS);
  } catch {
    return [];
  }
}

function persistQueuedTurns(turns) {
  const persistable = turns
    .filter((turn) => turn?.persistable !== false && !turn?.attachments?.length)
    .map(serializeQueuedTurn)
    .filter(Boolean)
    .slice(-MAX_PERSISTED_QUEUED_TURNS);
  if (!persistable.length) {
    localStorage.removeItem(QUEUED_TURNS_STORAGE_KEY);
    return;
  }
  localStorage.setItem(QUEUED_TURNS_STORAGE_KEY, JSON.stringify(persistable));
}

function serializeQueuedTurn(turn) {
  const rawPrompt = String(turn?.rawPrompt ?? "").trim();
  const prompt = String(turn?.prompt ?? "").trim();
  const conversationId = String(turn?.conversationId ?? "").trim();
  if (!rawPrompt || !prompt || !conversationId || isDraftConversationId(conversationId)) return null;
  return {
    id: String(turn.id ?? crypto.randomUUID()),
    conversationId,
    rawPrompt,
    displayPrompt: String(turn.displayPrompt ?? rawPrompt),
    prompt,
    mode: turn.mode === "review" ? "review" : null,
    reviewTarget: sanitizeQueuedReviewTarget(turn.reviewTarget),
    attachments: [],
    persistable: true,
    queuedAt: String(turn.queuedAt ?? new Date().toISOString())
  };
}

function normalizePersistedQueuedTurn(turn) {
  const serialized = serializeQueuedTurn(turn);
  if (!serialized) return null;
  return serialized;
}

function sanitizeQueuedReviewTarget(value) {
  if (!value || typeof value !== "object") return null;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return null;
  }
}

let refreshPromise = null;
let latestAuth = null;

function setAuth(value) {
  latestAuth = value;
  auth.value = value;
}

function makeApi(authRef) {
  return {
    get: (path) => request("GET", path, null, authRef),
    post: (path, body) => request("POST", path, body, authRef)
  };
}

async function request(method, path, body, authRef, canRefresh = true) {
  const currentAuth = authRef?.value ?? null;
  const headers = { accept: "application/json" };
  const requestPath = method === "GET" ? withNoCacheParam(path) : path;
  if (method === "GET") {
    headers["cache-control"] = "no-cache";
    headers.pragma = "no-cache";
  }
  if (body) headers["content-type"] = "application/json";
  if (currentAuth?.accessToken) headers.authorization = `Bearer ${currentAuth.accessToken}`;
  const response = await fetch(`${API_BASE}${requestPath}`, {
    method,
    cache: method === "GET" ? "no-store" : "default",
    headers,
    body: body ? JSON.stringify(body) : undefined
  });
  const data = await response.json().catch(() => ({}));
  if (response.status === 401 && canRefresh && currentAuth?.refreshToken && path !== "/api/auth/refresh") {
    try {
      const freshAuth = latestAuth?.refreshToken === currentAuth.refreshToken && latestAuth.accessToken !== currentAuth.accessToken
        ? latestAuth
        : await refreshAccessToken(currentAuth, authRef);
      return request(method, path, body, { value: freshAuth }, false);
    } catch {
      setAuth(null);
      throw new Error("登录已过期，请重新登录。");
    }
  }
  if (!response.ok) {
    throw new Error(data.error?.message ?? `请求失败：HTTP ${response.status}`);
  }
  return data;
}

async function requestStream(path, authRef, signal, canRefresh = true) {
  const currentAuth = authRef?.value ?? null;
  const headers = { accept: "text/event-stream", "cache-control": "no-cache", pragma: "no-cache" };
  if (currentAuth?.accessToken) headers.authorization = `Bearer ${currentAuth.accessToken}`;
  const response = await fetch(`${API_BASE}${withNoCacheParam(path)}`, { cache: "no-store", headers, signal });
  if (response.status === 401 && canRefresh && currentAuth?.refreshToken) {
    const freshAuth = latestAuth?.refreshToken === currentAuth.refreshToken && latestAuth.accessToken !== currentAuth.accessToken
      ? latestAuth
      : await refreshAccessToken(currentAuth, authRef);
    return requestStream(path, { value: freshAuth }, signal, false);
  }
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error?.message ?? `请求失败：HTTP ${response.status}`);
  }
  if (!response.body) {
    throw new Error("当前浏览器不支持运行流。");
  }
  return response;
}

function withNoCacheParam(path) {
  const separator = String(path).includes("?") ? "&" : "?";
  return `${path}${separator}_skybridge_ts=${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

async function readSse(response, onEvent) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";
    for (const part of parts) {
      const parsed = parseSseBlock(part);
      if (parsed) {
        await onEvent(parsed.event, parsed.data);
      }
    }
  }
  if (buffer.trim()) {
    const parsed = parseSseBlock(buffer);
    if (parsed) {
      await onEvent(parsed.event, parsed.data);
    }
  }
}

function parseSseBlock(block) {
  let event = "message";
  const dataLines = [];
  for (const line of String(block ?? "").split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim() || "message";
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }
  if (!dataLines.length) return null;
  try {
    return { event, data: JSON.parse(dataLines.join("\n")) };
  } catch {
    return null;
  }
}

async function refreshAccessToken(currentAuth, authRef) {
  if (!refreshPromise) {
    refreshPromise = request("POST", "/api/auth/refresh", { refreshToken: currentAuth.refreshToken }, null, false)
      .then((data) => {
        const freshAuth = { ...currentAuth, ...data };
        latestAuth = freshAuth;
        if (authRef) authRef.value = freshAuth;
        return freshAuth;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function useLocalState(key, initialValue) {
  const value = ref(readLocalState(key, initialValue));
  watch(value, (next) => {
    if (next === null || next === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(next));
    }
  }, { deep: true });
  return value;
}

function readLocalState(key, initialValue) {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? initialValue;
  } catch {
    return initialValue;
  }
}
</script>
